import { Button, Checkbox, Group, Modal, Stack, Textarea, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { SvgNegativeFeedback, SvgProvideFeedback } from '@mygpt/app/chat/components/FeedbackButton/icon';
import { feedbackType, messageRating } from '@mygpt/app/chat/services';
import { FEEDBACK_MAP } from '@mygpt/app/chat/constant';
import { useFeedbackModal } from '@mygpt/app/chat/hooks/useFeedbackModal';
import { useFeedbackButtonStore } from '@mygpt/app/chat/components/ChatContentBottomButton/store';
import { ratingType } from '@mygpt/types';

export interface onSubmitValue {
  feedbackContent: string;
  feedbackMetadata: {
    type: feedbackType;
    value: string[];
  };
}

interface FeedbackModalProps {
  type: feedbackType;
  opened: boolean;
  onClose: () => void;
  handleMessages: (obj: { rating: number; messageId: string }) => void;
}

// 定义一个接口来描述目标对象的类型
interface DynamicObject {
  [key: string]: boolean;
}

type FormValuesKey = `checked_${number}`;

export interface FormValues {
  feedback: string;
  [key: FormValuesKey]: boolean; // 使用模板字面量类型定义动态键
}

export const useGetFeedbackCheckbox = () => {
  const { t } = useTranslation();

  return {
    good: FEEDBACK_MAP.good
      .filter((item) => !item.old)
      .map((item) => ({
        ...item,
        label: t(item.label),
      })),
    bad: FEEDBACK_MAP.bad
      .filter((item) => !item.old)
      .map((item) => ({
        ...item,
        label: t(item.label),
      })),
  };
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({ onClose, opened, type, handleMessages }) => {
  const { t } = useTranslation();
  const feedbackCheckboxs = useGetFeedbackCheckbox();
  const { currentMessageInfo, modalType } = useFeedbackModal();
  const { showAlert } = useFeedbackButtonStore();
  const { aiId, sessionId, messageId, rating, pageSource } = currentMessageInfo;

  const onSubmit = (values: onSubmitValue) => {
    const param = {
      ai_id: aiId,
      session_id: sessionId,
      message_id: messageId,
      rating: (modalType === 'good' ? 2 : -1) as ratingType,
      source: pageSource,

      feedback_content: values.feedbackContent,
      feedback_metadata: values.feedbackMetadata,
    };

    return messageRating(param)
      .then(() => {
        handleMessages && handleMessages({ rating: param.rating, messageId });
        return true;
      })
      .catch((error) => {
        console.error(error);
        const detail = error?.response?.data?.detail;
        showNotification({
          color: 'red',
          message: detail || t('errors.common'),
        });
        return false;
      });
  };

  const formCheckboxList = feedbackCheckboxs[type].reduce<DynamicObject>((obj, _, index) => {
    obj[`checked_${index}`] = false;
    return obj;
  }, {});
  const form = useForm<FormValues>({
    initialValues: {
      ...formCheckboxList,
      feedback: '',
    },
  });

  const handleSubmit = async () => {
    const feedbackMetadataIndex = [];
    for (const key in form.values) {
      if (!key.startsWith('checked')) continue;
      if (form.values[key as FormValuesKey]) {
        feedbackMetadataIndex.push(key.replace(/^checked_/, ''));
      }
    }

    const feedbackMetadataValue = feedbackMetadataIndex.map((item) => {
      return feedbackCheckboxs[type][Number(item)].value;
    });

    const value = {
      feedbackContent: form.values.feedback,
      feedbackMetadata: {
        type,
        value: feedbackMetadataValue,
      },
    };
    // 等待评分真正写盘成功后再关弹窗/提示，避免网络或后端失败时静默丢失反馈
    const ok = await onSubmit(value);
    if (!ok) return;
    onClose(); // 提交后关闭模态框
    form.reset();
    showAlert(messageId);
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={
        <Group>
          {type === 'good' ? <SvgProvideFeedback /> : <SvgNegativeFeedback />}{' '}
          <Title order={4}>{t('chat_feedback.title')}</Title>
        </Group>
      }
    >
      <form>
        <Stack gap={'md'} mt={20}>
          {feedbackCheckboxs[type].map((item, index) => (
            <Checkbox
              key={index}
              label={item.label}
              checked={form.values[`checked_${index}`]}
              {...form.getInputProps(`checked_${index}`)}
            />
          ))}

          <Textarea
            mt={10}
            autosize
            minRows={2}
            maxRows={10}
            placeholder={t('chat_feedback.placeholder')}
            {...form.getInputProps('feedback')}
          />

          <Group justify="flex-end" mt="md">
            <Button onClick={handleSubmit}>{t('submit')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default FeedbackModal;
